'use client';

import { useState } from 'react';
import {
  BriefcaseIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  PhoneIcon,
  SparklesIcon,
  XMarkIcon,
  HandThumbUpIcon,
  HandThumbDownIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@components/common/Forms';
import { useTranslation } from 'react-i18next';
import type { CvFile, MatchColors, ManualStatusType } from '../screening.types';
import { MATCH_COLORS } from '../screening.constants';
import { InterviewTranscriptModal } from '../InterviewTranscriptModal/InterviewTranscriptModal';
import { InterviewAudioPlayer } from '../InterviewAudioPlayer/InterviewAudioPlayer';
import { useUpdateManualStatus } from '@hooks/useSwrApi/endpoints/screening';

interface CandidateProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: CvFile | null;
  jobId?: number;
  onStatusChanged?: () => void;
}

const getInterviewStatusBadge = (interviewStatus: CvFile['interviewStatus'], t: (key: string) => string) => {
  if (!interviewStatus) return null;

  const statusLower = interviewStatus.toLowerCase();

  if (statusLower === 'queued' || statusLower === 'interview_queued') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-700">
        <ClockIcon className="size-4" />
        {t('screening.interview.queued')}
      </span>
    );
  }

  if (statusLower === 'in_progress' || statusLower === 'interview_in_progress') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-yellow-100 px-3 py-1 text-sm font-medium text-yellow-700">
        <ChatBubbleLeftRightIcon className="size-4" />
        {t('screening.interview.in.progress')}
      </span>
    );
  }

  if (statusLower === 'completed' || statusLower === 'interview_completed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
        <CheckCircleIcon className="size-4" />
        {t('screening.interview.complete')}
      </span>
    );
  }

  if (statusLower === 'scheduled' || statusLower === 'interview_scheduled') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-3 py-1 text-sm font-medium text-purple-700">
        <ChatBubbleLeftRightIcon className="size-4" />
        {t('screening.interview.scheduled')}
      </span>
    );
  }

  if (statusLower.includes('failed') || statusLower.includes('skipped')) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700">
        <ExclamationCircleIcon className="size-4" />
        {t('screening.interview.failed')}
      </span>
    );
  }

  return null;
};

export const CandidateProfileModal = ({
  isOpen,
  onClose,
  file,
  jobId,
  onStatusChanged,
}: CandidateProfileModalProps) => {
  const { t } = useTranslation();
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const { updateStatus, isLoading: isStatusLoading } = useUpdateManualStatus();
  const [localManualStatus, setLocalManualStatus] = useState<ManualStatusType | null>(null);

  // Initialize local status from file
  useState(() => {
    if (file?.manualStatus) {
      setLocalManualStatus(file.manualStatus);
    }
  });

  if (!isOpen || !file) return null;

  const currentManualStatus = localManualStatus || file.manualStatus;

  const handleStatusChange = async (newStatus: ManualStatusType) => {
    if (!jobId || !file.phone) {
      return;
    }

    const success = await updateStatus({
      jobId,
      phone: file.phone,
      manualStatus: newStatus,
    });

    if (success) {
      setLocalManualStatus(newStatus);
      onStatusChanged?.();
    }
  };

  const colors: MatchColors | null = file.matchLevel ? MATCH_COLORS[file.matchLevel] : null;

  // Check if interview is completed and has an interviewId
  const isInterviewCompleted = file.interviewStatus?.toLowerCase() === 'completed'
    || file.interviewStatus?.toLowerCase() === 'interview_completed';
  const hasInterviewData = isInterviewCompleted && file.interviewId;

  // Get initials from name
  const getInitials = (name: string) => name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative z-10 m-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
        >
          <XMarkIcon className="size-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Header */}
          <div className="mb-6">
            <h2 className="text-xl font-bold text-slate-800">
              {t('screening.candidate.profile')}
            </h2>
            <p className="text-sm text-slate-500">
              {(t('screening.extracted.from') || 'Extracted from {fileName}').replace('{fileName}', file.name)}
            </p>
          </div>

          <div className="space-y-6">
            {/* Profile Header */}
            <div className="flex items-start gap-4">
              <div
                className={`flex size-16 items-center justify-center rounded-2xl text-xl font-bold text-white ${
                  colors ? 'bg-gradient-to-br from-emerald-500 to-green-500' : 'bg-gradient-to-br from-slate-400 to-slate-500'
                }`}
              >
                {file.displayName ? getInitials(file.displayName) : 'CV'}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-800">
                  {file.displayName || file.name}
                </h3>
                {file.headline && (
                  <p className="text-slate-600">{file.headline}</p>
                )}
              </div>
              {file.matchLevel && colors && (
                <span className={`rounded-lg px-3 py-1 text-xs font-semibold capitalize ${colors.badge}`}>
                  {(t('screening.match.level') || '{matchLevel}').replace('{matchLevel}', file.matchLevel)}
                </span>
              )}
            </div>

            {/* Score Badges */}
            {(file.score != null || file.interviewScore != null || file.finalScore != null) && colors && (
              <div className="flex flex-wrap items-center gap-2">
                {file.score != null && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                    {t('screening.score.match.cv')}
                    {': '}
                    {file.score}
                    {'%'}
                  </span>
                )}
                {file.interviewScore != null && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                    {t('screening.score.interview')}
                    {': '}
                    {file.interviewScore}
                    {'%'}
                  </span>
                )}
                {file.finalScore != null && (
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${colors.badge} bg-opacity-10`}>
                    {t('screening.score.final')}
                    {': '}
                    {file.finalScore}
                    {'%'}
                  </span>
                )}
              </div>
            )}

            {/* Interview Status */}
            {file.interviewStatus && (
              <div className="flex items-center gap-3">
                {getInterviewStatusBadge(file.interviewStatus, t)}
              </div>
            )}

            {/* Interview Transcript & Audio - shown for completed interviews */}
            {hasInterviewData && (
              <div className="space-y-3">
                {/* View Transcript Link */}
                <button
                  type="button"
                  onClick={() => setIsTranscriptOpen(true)}
                  className="flex items-center gap-2 text-sm font-medium text-emerald-600 transition-colors hover:text-emerald-700"
                >
                  <DocumentTextIcon className="size-4" />
                  {t('screening.view.transcript')}
                </button>

                {/* Audio Player */}
                <InterviewAudioPlayer interviewId={file.interviewId!} />
              </div>
            )}

            {/* Contact Info */}
            {(file.email || file.phone) && (
              <div className="grid grid-cols-2 gap-4">
                {file.email && (
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                    <EnvelopeIcon className="size-4 text-slate-400" />
                    <span className="truncate text-sm text-slate-600">{file.email}</span>
                  </div>
                )}
                {file.phone && (
                  <div className="flex items-center gap-3 rounded-lg bg-slate-50 p-3">
                    <PhoneIcon className="size-4 text-slate-400" />
                    <span className="text-sm text-slate-600">{file.phone}</span>
                  </div>
                )}
              </div>
            )}

            {/* KEY INSIGHTS Section */}
            {file.matchingSummary && (
              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  <ExclamationTriangleIcon className="size-4" />
                  {t('screening.key.insights')}
                </h4>
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-sm font-medium text-slate-700">{file.matchingSummary}</p>
                </div>
              </div>
            )}

            {/* AI Insights */}
            {file.insights && file.insights.length > 0 && (
              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  <SparklesIcon className="size-4" />
                  {t('screening.ai.insights')}
                </h4>
                <ul className="space-y-2">
                  {file.insights.map((insight) => (
                    <li
                      key={`modal-insight-${insight.substring(0, 20)}`}
                      className="flex items-start gap-2 text-sm text-slate-600"
                    >
                      <CheckCircleIcon className="mt-0.5 size-4 shrink-0 text-emerald-500" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Interview Summary */}
            {file.interviewSummary && (
              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  <BriefcaseIcon className="size-4" />
                  {t('screening.interview.summary')}
                </h4>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-sm text-slate-600">{file.interviewSummary}</p>
                </div>
              </div>
            )}

            {/* Manual Status Selection - Only show if we have jobId and phone */}
            {jobId && file.phone && (
              <div>
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
                  <UserGroupIcon className="size-4" />
                  {t('screening.manual.status.title', 'Candidate Status')}
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => handleStatusChange('SHORTLIST')}
                    disabled={isStatusLoading}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all disabled:opacity-50 ${
                      currentManualStatus === 'SHORTLIST'
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'
                    }`}
                  >
                    <HandThumbUpIcon className={`size-6 ${currentManualStatus === 'SHORTLIST' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${currentManualStatus === 'SHORTLIST' ? 'text-emerald-700' : 'text-slate-600'}`}>
                      {t('screening.status.shortlist', 'Shortlist')}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleStatusChange('INTERVIEW')}
                    disabled={isStatusLoading}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all disabled:opacity-50 ${
                      currentManualStatus === 'INTERVIEW'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    <ChatBubbleLeftRightIcon className={`size-6 ${currentManualStatus === 'INTERVIEW' ? 'text-blue-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${currentManualStatus === 'INTERVIEW' ? 'text-blue-700' : 'text-slate-600'}`}>
                      {t('screening.status.interview', 'Interview')}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleStatusChange('REJECTED')}
                    disabled={isStatusLoading}
                    className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-all disabled:opacity-50 ${
                      currentManualStatus === 'REJECTED'
                        ? 'border-red-500 bg-red-50'
                        : 'border-slate-200 bg-white hover:border-red-300 hover:bg-red-50/50'
                    }`}
                  >
                    <HandThumbDownIcon className={`size-6 ${currentManualStatus === 'REJECTED' ? 'text-red-600' : 'text-slate-400'}`} />
                    <span className={`text-sm font-medium ${currentManualStatus === 'REJECTED' ? 'text-red-700' : 'text-slate-600'}`}>
                      {t('screening.status.rejected', 'Rejected')}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 border-t border-slate-100 pt-4">
              <Button
                color="primary"
                styling="outline"
                rounding="normal"
                className="flex-1 font-semibold"
                onClick={onClose}
              >
                {t('global.btn.close')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Transcript Modal */}
      {hasInterviewData && (
        <InterviewTranscriptModal
          isOpen={isTranscriptOpen}
          onClose={() => setIsTranscriptOpen(false)}
          interviewId={file.interviewId!}
          candidateName={file.displayName}
        />
      )}
    </div>
  );
};

export default CandidateProfileModal;
